import React, { memo, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Stack, Pagination } from 'investira.react.components';
import FilterMetricasEmpresa from './FilterMetricasEmpresa';
import ListEmpresasMetricas from './ListEmpresasMetricas';

/**
 * Renderiza o componente EmpresasMetricas.
 *
 * @param {Object} props - Propriedades do componente.
 * @param {boolean} props.isLoading - Propriedade `isLoading` do componente.
 * @param {Array} props.data - Propriedade `data` do componente.
 * @param {Function} props.onClick - Propriedade `onClick` do componente.
 * @param {Object} props.filters - Propriedade `filters` do componente.
 * @param {Function} props.onChangeFilter - Propriedade `onChangeFilter` do componente.
 * @param {number} props.pageSize - Propriedade `pageSize` do componente.
 * @param {boolean} props.showFilter - Propriedade `showFilter` do componente.
 * @param {boolean} props.showPagination - Propriedade `showPagination` do componente.
 * @param {Object} props.filterConfig - Propriedade `filterConfig` do componente.
 * @param {boolean} props.showFaixa - Propriedade `showFaixa` do componente.
 * @param {boolean} props.showSimilaridade - Propriedade `showSimilaridade` do componente.
 * @param {React.ReactNode} props.header - Propriedade `header` do componente.
 * @param {boolean} props.showSectors - Propriedade `showSectors` do componente.
 * @param {Function} props.onPageChange - Propriedade `onPageChange` do componente.
 *
 * @description
 * Este componente realiza as seguintes operacoes:
 * 1. Recebe as propriedades necessarias para compor a interface
 * 2. Organiza os estados e callbacks usados durante a renderizacao
 * 3. Renderiza a experiencia visual do organismo
 *
 * @returns {JSX.Element|null} Elemento renderizado pelo componente.
 */

const EmpresasMetricas = memo(props => {
  const {
    isLoading,
    data,
    onClick,
    filters,
    onChangeFilter,
    pageSize = 10,
    showFilter = true,
    showPagination = true,
    filterConfig = { showFaixa: true, showSimilaridade: true },
    header,
    showSectors = true,
    selectedId,
    onPageChange
  } = props;

  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [data, filters]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const paginatedData = useMemo(() => {
    if (!showPagination || !data) return data || [];
    return data.slice((page - 1) * pageSize, page * pageSize);
  }, [data, page, pageSize, showPagination]);

  useEffect(() => {
    if (onPageChange && paginatedData.length > 0) {
      onPageChange(paginatedData[0]);
    }
  }, [paginatedData, onPageChange]);

  return (
    <Stack spacing={2}>
      {(showFilter || header) && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={header ? 'space-between' : 'flex-start'}
          spacing={2}>
          {header}
          {showFilter && (
            <FilterMetricasEmpresa
              values={filters}
              onValuesChange={onChangeFilter}
              showFaixa={filterConfig.showFaixa}
              showSimilaridade={filterConfig.showSimilaridade}
            />
          )}
        </Stack>
      )}
      <ListEmpresasMetricas
        isLoading={isLoading}
        data={paginatedData}
        onClick={onClick}
        metrica={filters?.metrica}
        showSectors={showSectors}
        selectedId={selectedId}
      />
      {showPagination && !isLoading && data && data.length > pageSize && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination
            count={Math.ceil(data.length / pageSize)}
            page={page}
            onChange={handleChangePage}
            color="primary"
            size="small"
          />
        </Stack>
      )}
    </Stack>
  );
});

EmpresasMetricas.propTypes = {
  isLoading: PropTypes.bool,
  data: PropTypes.array,
  onClick: PropTypes.func,
  filters: PropTypes.object,
  onChangeFilter: PropTypes.func,
  pageSize: PropTypes.number,
  showFilter: PropTypes.bool,
  showPagination: PropTypes.bool,
  filterConfig: PropTypes.shape({
    showFaixa: PropTypes.bool,
    showSimilaridade: PropTypes.bool
  }),
  header: PropTypes.node,
  showSectors: PropTypes.bool,
  selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onPageChange: PropTypes.func
};

export default EmpresasMetricas;
